import { FC } from 'react';
import {
  Button,
  TextTitle,
  TextBody,
  Spacer,
  Stack,
  BlockList,
  Input,
} from '@pypestream/design-system';
import { FieldValues, SubmitHandler, useFormContext } from 'react-hook-form';

export const StepConfig: FC<{
  nextStep: SubmitHandler<FieldValues>;
  backStep?: () => void;
  disabled?: boolean;
}> = ({ nextStep, backStep, disabled = false }) => {
  const { handleSubmit, register, watch, setValue } = useFormContext();
  const supervisorName = watch('supervisorName');
  const isSolutionNameEmpty = !supervisorName || supervisorName.trim() === '';

  // const watchedTemperature = watch('temperature');
  // const watchedMaxOutputTokens = watch('maxOutputTokens');
  // const watchedTopP = watch('topP');
  // const watchedTopK = watch('topK');

  return (
    <form onSubmit={(e) => e.preventDefault()}>
      <TextTitle size="small" textAlign="center">
        Solution Name
      </TextTitle>
      <Spacer size="small" />
      <TextBody variant="secondary" textAlign="center">
        How the solution will refer to itself, e.g. Hi, I’m Emily your digital
        assistant.
      </TextBody>
      <Spacer size="xlarge" />

      <BlockList>
        {/**
        <div>
          <Stack alignItems="center" gutter="2xsmall">
            <TextBody size="small">Brand Name</TextBody>
            <Tooltip
              placement="right"
              content="The name of your company or brand. This helps the model respond in a way that aligns with your brand identity."
            >
              <Icon name="info" color="gray" size="xsmall" />
            </Tooltip>
          </Stack>
          <Spacer size="2xsmall" />
          <Input
            variant="outlined"
            placeholder="Brand Name"
            {...register('brandName')}
          />
        </div>
         */}
        <div>
          <Stack alignItems="center" gutter="2xsmall">
            <TextBody size="small">Solution Name*</TextBody>
          </Stack>
          <Spacer size="2xsmall" />
          <Input
            test-id="create-solution-modal-solution-supervisor-name-input"
            variant="outlined"
            placeholder="Emily, your digital assistant"
            {...register('supervisorName', { required: true })}
            onInput={(e) => {
              // @ts-expect-error - TODO: fix this
              const { value } = e.target;
              setValue('supervisorName', value, {
                shouldValidate: true,
                shouldDirty: true,
              });
            }}
          />
        </div>
        {/**
        <div>
          <Stack alignItems="center" gutter="2xsmall">
            <TextBody size="small">Tone</TextBody>
            <Tooltip
              placement="right"
              content="Set the personality or communication style for the supervisor's responses—e.g., professional, friendly, or playful."
            >
              <Icon name="info" color="gray" size="xsmall" />
            </Tooltip>
          </Stack>
          <Spacer size="2xsmall" />
          <Textarea
            variant="outlined"
            placeholder="Tone"
            rows={3}
            {...register('tone')}
          ></Textarea>
        </div>
         */}
        {/* <div>
          <Stack alignItems="center" gutter="2xsmall">
            <TextBody size="small">Other Information</TextBody>
            <Tooltip
              placement="right"
              content="Any additional context or instructions to guide how the Supervisor Agent behaves."
            >
              <Icon name="info" color="gray" size="xsmall" />
            </Tooltip>
          </Stack>
          <Spacer size="2xsmall" />
          <Textarea
            variant="outlined"
            placeholder="Other Information"
            rows={3}
            {...register('otherInformation')}
          ></Textarea>
        </div> */}
      </BlockList>
      {/**
      <Spacer size="large" />
      <Grid gutter="large" rowGutter="large">
        <GridCell xsmall="6">
          <Stack justifyContent="space-between" alignItems="center">
            <Stack alignItems="center" gutter="2xsmall">
              <TextBody size="small">Temperature</TextBody>
              <Tooltip
                placement="right"
                content="Controls the randomness of the model's responses. Higher values (e.g., 0.8) make it more creative; lower values (e.g., 0.2) make it more focused and predictable."
              >
                <Icon name="info" color="gray" size="xsmall" />
              </Tooltip>
            </Stack>
            <TextBody size="small">{watchedTemperature ?? 0.2}</TextBody>
          </Stack>
          <Slider
            showMinMaxLabels={false}
            step={0.1}
            min={0}
            max={1}
            value={watchedTemperature ?? 0.2}
            // @ts-expect-error - TODO: fix this
            onInput={(e) => setValue('temperature', parseFloat(e.target.value))}
          />
        </GridCell>
        <GridCell xsmall="6">
          <Stack justifyContent="space-between" alignItems="center">
            <Stack alignItems="center" gutter="2xsmall">
              <TextBody size="small">Max output tokens</TextBody>
              <Tooltip
                placement="right"
                content="Sets the maximum length of the model's responses. One token is roughly 3/4 of a word."
              >
                <Icon name="info" color="gray" size="xsmall" />
              </Tooltip>
            </Stack>
            <TextBody size="small">{watchedMaxOutputTokens ?? 2048}</TextBody>
          </Stack>
          <Slider
            showMinMaxLabels={false}
            step={1}
            min={0}
            max={5000}
            value={watchedMaxOutputTokens ?? 2048}
            // @ts-expect-error - TODO: fix this
            onInput={(e) =>
              setValue('maxOutputTokens', parseInt(e.target.value))
            }
          />
        </GridCell>
        <GridCell xsmall="6">
          <Stack justifyContent="space-between" alignItems="center">
            <Stack alignItems="center" gutter="2xsmall">
              <TextBody size="small">Top P</TextBody>
              <Tooltip
                placement="right"
                content="Controls the diversity of responses by narrowing down the model's choices to the most likely ones. Lower values limit variation."
              >
                <Icon name="info" color="gray" size="xsmall" />
              </Tooltip>
            </Stack>
            <TextBody size="small">{watchedTopP ?? 0.8}</TextBody>
          </Stack>
          <Slider
            showMinMaxLabels={false}
            step={0.1}
            min={0}
            max={1}
            value={watchedTopP ?? 0.8}
            // @ts-expect-error - TODO: fix this
            onInput={(e) => setValue('topP', parseFloat(e.target.value))}
          />
        </GridCell>
        <GridCell xsmall="6">
          <Stack justifyContent="space-between" alignItems="center">
            <Stack alignItems="center" gutter="2xsmall">
              <TextBody size="small">Top K</TextBody>
              <Tooltip
                placement="right"
                content="Limits the model to selecting from the top K most likely next words. Smaller values reduce randomness and increase focus."
              >
                <Icon name="info" color="gray" size="xsmall" />
              </Tooltip>
            </Stack>

            <TextBody size="small">{watchedTopK ?? 0.2}</TextBody>
          </Stack>
          <Slider
            showMinMaxLabels={false}
            step={0.1}
            min={0}
            max={1}
            value={watchedTopK ?? 0.2}
            // @ts-expect-error - TODO: fix this
            onInput={(e) => setValue('topK', parseFloat(e.target.value))}
          />
        </GridCell>
      </Grid>
       */}

      <Spacer size="xlarge" />
      <Stack justifyContent="space-between">
        <Button
          size="large"
          variant="ghost"
          onClick={backStep}
          disabled={disabled}
        >
          Back
        </Button>
        <Stack gutter="small">
          <Button
            test-id="create-solution-step-config-next-btn"
            size="large"
            variant="primary"
            onClick={handleSubmit(nextStep)}
            disabled={disabled || isSolutionNameEmpty}
          >
            Next
          </Button>
        </Stack>
      </Stack>
    </form>
  );
};
